import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

/**
 * Block alerts use `border-2` + tinted background; body text is slate-900 so it does not
 * mirror the semantic hue (same approach as status badges, but heavier border).
 */
const alertVariants = cva("relative w-full rounded-lg border-2 p-4 text-sm text-slate-900", {
  variants: {
    variant: {
      default: "border-slate-200 bg-slate-50",
      destructive: "border-red-200 bg-red-50",
      success: "border-emerald-200 bg-emerald-50",
      warning: "border-amber-200 bg-amber-50",
      info: "border-blue-200 bg-blue-50",
    },
  },
  defaultVariants: {
    variant: "default",
  },
});

export interface AlertProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof alertVariants> {}

const Alert = React.forwardRef<HTMLDivElement, AlertProps>(
  ({ className, variant, ...props }, ref) => (
    <div ref={ref} role="alert" className={cn(alertVariants({ variant }), className)} {...props} />
  )
);
Alert.displayName = "Alert";

const AlertDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "text-sm text-slate-900 [&_p]:leading-relaxed [&_a]:font-medium [&_a]:text-primary [&_a]:underline",
      className
    )}
    {...props}
  />
));
AlertDescription.displayName = "AlertDescription";

export { Alert, AlertDescription, alertVariants };
